import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Pencil, Ban, CalendarDays, MapPin, Filter, BellRing, CheckCircle, XCircle, History, ChevronUp, ChevronDown } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { Button } from "@/shared/components/Button";
import { Paginacao } from "@/shared/components/Paginacao";
import { useAuth } from "@/core/hooks/useAuth";
import { useGestaoModulo } from "@/core/hooks/useGestaoModulo";
import { useMinisteriosQueLidero } from "@/core/hooks/useMinisterios";
import { rotuloDiaLongo, formatarHora, chaveDia } from "@/shared/lib/datas";
import { FormularioEvento } from "@/modules/eventos/components/FormularioEvento";
import { useAmbientesAtivos } from "@/modules/reservas/hooks/useAmbientes";
import { useMinisterioOpcoes } from "@/modules/reservas/hooks/useMinisterioOpcoes";
import {
  useEventos,
  useCriarEvento,
  useAtualizarEvento,
  useCancelarEvento,
  useAprovarEvento,
  useRejeitarEvento,
} from "@/modules/eventos/hooks/useEventos";
import type { DadosEvento, EventoComRelacoes } from "@/modules/eventos/services/eventosService";

const TAM_PAGINA = 10;

export function GerenciarEventos() {
  const { pessoa, podeGerenciarEventos } = useAuth();
  const { data: eventos, isLoading } = useEventos();
  const { data: ambientes } = useAmbientesAtivos();
  const { data: ministeriosLiderados } = useMinisteriosQueLidero();
  const { modoDados: modoDadosEventos, ministeriosGestao: ministeriosGestaoEventos } =
    useGestaoModulo("eventos");
  const ministerioOpcoes = useMinisterioOpcoes("eventos", podeGerenciarEventos);
  const criar = useCriarEvento();
  const atualizar = useAtualizarEvento();
  const cancelar = useCancelarEvento();
  const aprovar = useAprovarEvento();
  const rejeitar = useRejeitarEvento();

  const agora = useMemo(() => new Date(), []);

  const [modalAberto, setModalAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<EventoComRelacoes | null>(null);
  const [erroForm, setErroForm] = useState<string | null>(null);
  // Reteste 22-23/07/2026: Evento agora "Cancela" (soft) no lugar de "Excluir".
  const [paraCancelar, setParaCancelar] = useState<EventoComRelacoes | null>(null);
  const [erroCancelamento, setErroCancelamento] = useState<string | null>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  // F4-04b: aprovar evento passa por modal de confirmação (como Reservas).
  const [paraAprovar, setParaAprovar] = useState<EventoComRelacoes | null>(null);
  const [paraRejeitar, setParaRejeitar] = useState<EventoComRelacoes | null>(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState("");
  const [erroRejeicao, setErroRejeicao] = useState<string | null>(null);
  const [detalhes, setDetalhes] = useState<EventoComRelacoes | null>(null);
  const [mensagemSucesso, setMensagemSucesso] = useState<string | null>(null);
  const [mensagemErro, setMensagemErro] = useState<string | null>(null);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [paginaHist, setPaginaHist] = useState(1);

  useEffect(() => {
    if (!mensagemSucesso) return;
    const t = setTimeout(() => setMensagemSucesso(null), 6000);
    return () => clearTimeout(t);
  }, [mensagemSucesso]);

  useEffect(() => {
    if (!mensagemErro) return;
    const t = setTimeout(() => setMensagemErro(null), 6000);
    return () => clearTimeout(t);
  }, [mensagemErro]);

  const [searchParams, setSearchParams] = useSearchParams();
  const filtroMinisterio = searchParams.get("ministerio") ?? "";
  const filtroDia = searchParams.get("dia") ?? "";
  const filtroPeriodo = (searchParams.get("periodo") ?? "") as "" | "manha" | "tarde" | "noite";

  function setFiltro(chave: "ministerio" | "dia" | "periodo", valor: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (valor) next.set(chave, valor);
        else next.delete(chave);
        return next;
      },
      { replace: true },
    );
  }

  const ministeriosNaLista = useMemo(() => {
    const nomes = new Map<string, string>();
    for (const ev of eventos ?? []) {
      for (const m of ev.ministerios) nomes.set(m.id, m.nome);
    }
    return Array.from(nomes.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [eventos]);

  function periodoDoHorario(iso: string): "manha" | "tarde" | "noite" {
    const hora = new Date(iso).getHours();
    if (hora < 12) return "manha";
    if (hora < 18) return "tarde";
    return "noite";
  }

  const eventosFiltrados = useMemo(() => {
    return (eventos ?? []).filter((ev) => {
      if (filtroMinisterio && !ev.ministerios.some((m) => m.id === filtroMinisterio)) return false;
      if (filtroDia && chaveDia(ev.inicio) !== filtroDia) return false;
      if (filtroPeriodo && periodoDoHorario(ev.inicio) !== filtroPeriodo) return false;
      return true;
    });
  }, [eventos, filtroMinisterio, filtroDia, filtroPeriodo]);

  // Reteste: um evento vai para o HISTÓRICO se foi cancelado/recusado OU já
  // passou (fim < agora). O restante fica na lista de Ativos.
  function ehHistorico(ev: EventoComRelacoes): boolean {
    return ev.status === "cancelado" || ev.status === "recusado" || new Date(ev.fim) < agora;
  }
  function rotuloStatus(ev: EventoComRelacoes): { label: string; cls: string } {
    const passado = new Date(ev.fim) < agora;
    if (ev.status === "cancelado") return { label: "Cancelado", cls: "bg-surface-2 text-muted" };
    if (ev.status === "recusado") return { label: "Recusado", cls: "bg-danger/12 text-danger" };
    if (ev.status === "pendente")
      return passado
        ? { label: "Expirado", cls: "bg-surface-2 text-muted" }
        : { label: "Pendente", cls: "bg-warning/15 text-warning" };
    return passado
      ? { label: "Realizado", cls: "bg-primary/10 text-primary" }
      : { label: "Confirmado", cls: "bg-success/10 text-success" };
  }

  const eventosAtivos = useMemo(
    () => eventosFiltrados.filter((ev) => !ehHistorico(ev)).sort((a, b) => a.inicio.localeCompare(b.inicio)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventosFiltrados],
  );
  const eventosHistorico = useMemo(
    () => eventosFiltrados.filter((ev) => ehHistorico(ev)).sort((a, b) => b.inicio.localeCompare(a.inicio)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventosFiltrados],
  );
  const totalPaginasHist = Math.max(1, Math.ceil(eventosHistorico.length / TAM_PAGINA));
  const paginaHistOk = Math.min(paginaHist, totalPaginasHist);
  const historicoPagina = eventosHistorico.slice(
    (paginaHistOk - 1) * TAM_PAGINA,
    paginaHistOk * TAM_PAGINA,
  );

  const filtrosAtivos = !!filtroMinisterio || !!filtroDia || !!filtroPeriodo;
  function limparFiltros() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("ministerio");
      next.delete("dia");
      next.delete("periodo");
      return next;
    }, { replace: true });
  }

  // Sprint 3 (22/07/2026): abrir edição/cancelamento vindo do Calendário Central
  // (?editar=<id> / ?cancelar=<id>). Reaproveita o modal/permissão desta tela.
  useEffect(() => {
    const editarId = searchParams.get("editar");
    const cancelarId = searchParams.get("cancelar");
    if ((!editarId && !cancelarId) || !eventos) return;
    const alvo = eventos.find((ev) => ev.id === (editarId ?? cancelarId));
    if (alvo && podeGerirEvento(alvo)) {
      if (editarId) abrirEdicao(alvo);
      else setParaCancelar(alvo);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("editar");
        next.delete("cancelar");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventos]);

  const podeGerirModulo = podeGerenciarEventos;
  const podeCriar = podeGerirModulo || ministerioOpcoes.length > 0;
  function podeGerirEvento(ev: EventoComRelacoes): boolean {
    const ministeriosDoEvento = [ev.ministerio_id, ...ev.ministerios.map((m) => m.id)].filter(
      (id): id is string => !!id,
    );
    if (podeGerirModulo) {
      if (modoDadosEventos !== "individual") return true;
      if (ministeriosDoEvento.some((id) => ministeriosGestaoEventos.includes(id))) return true;
    }
    if (ministeriosDoEvento.some((id) => (ministeriosLiderados ?? []).some((m) => m.id === id))) {
      return true;
    }
    return false;
  }

  function abrirCriacao() {
    setEmEdicao(null);
    setErroForm(null);
    setModalAberto(true);
  }
  function abrirEdicao(ev: EventoComRelacoes) {
    setEmEdicao(ev);
    setErroForm(null);
    setModalAberto(true);
  }

  async function handleSubmit(dados: DadosEvento) {
    setErroForm(null);
    try {
      if (emEdicao) {
        await atualizar.mutateAsync({ id: emEdicao.id, dados });
        setMensagemSucesso("Evento atualizado!");
      } else {
        const resultado = await criar.mutateAsync({ ...dados, criadoPorPessoaId: pessoa?.id ?? null });
        setMensagemSucesso(
          resultado.status === "pendente"
            ? "Evento enviado para aprovação de quem gerencia Eventos."
            : "Evento criado!",
        );
      }
      setModalAberto(false);
    } catch (e) {
      setErroForm(e instanceof Error ? e.message : "Não foi possível salvar o evento.");
    }
  }

  async function confirmarCancelamento() {
    if (!paraCancelar) return;
    setErroCancelamento(null);
    try {
      await cancelar.mutateAsync({ id: paraCancelar.id, motivo: motivoCancelamento.trim() });
      setMensagemSucesso("Evento cancelado.");
      setParaCancelar(null);
      setMotivoCancelamento("");
    } catch (e) {
      setErroCancelamento(e instanceof Error ? e.message : "Não foi possível cancelar o evento.");
    }
  }

  async function confirmarAprovacao() {
    if (!paraAprovar) return;
    setMensagemErro(null);
    try {
      await aprovar.mutateAsync(paraAprovar.id);
      setMensagemSucesso(`Evento "${paraAprovar.titulo}" aprovado.`);
      setParaAprovar(null);
    } catch (e) {
      setMensagemErro(e instanceof Error ? e.message : "Não foi possível aprovar o evento.");
      setParaAprovar(null);
    }
  }

  async function confirmarRejeicao() {
    if (!paraRejeitar) return;
    setErroRejeicao(null);
    try {
      await rejeitar.mutateAsync({ id: paraRejeitar.id, motivo: motivoRejeicao.trim() });
      setMensagemSucesso("Evento recusado — quem criou foi avisado.");
      setParaRejeitar(null);
      setMotivoRejeicao("");
    } catch (e) {
      setErroRejeicao(e instanceof Error ? e.message : "Não foi possível recusar o evento.");
    }
  }

  function itemEvento(ev: EventoComRelacoes, comAcoes: boolean, idx: number) {
    const st = rotuloStatus(ev);
    return (
      <li
        key={ev.id}
        onClick={() => setDetalhes(ev)}
        className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-2 ${idx > 0 ? "border-t border-border" : ""}`}
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-info/10 text-info">
          <CalendarDays size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-text">{ev.titulo}</p>
            <span
              className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                ev.visibilidade === "publico" ? "bg-primary/10 text-primary" : "bg-surface-2 text-muted"
              }`}
            >
              {ev.visibilidade === "publico" ? "Público" : "Privado"}
            </span>
            <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${st.cls}`}>
              {st.label}
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted">
            {rotuloDiaLongo(ev.inicio)}
            {chaveDia(ev.fim) !== chaveDia(ev.inicio) && ` – ${rotuloDiaLongo(ev.fim)}`}
            {!ev.dia_inteiro && ` · ${formatarHora(ev.inicio)}`}
            {ev.ambiente_nome && (
              <span className="inline-flex items-center gap-0.5">
                {" "}
                · <MapPin size={11} /> {ev.ambiente_nome}
              </span>
            )}{" "}
            · {ev.ministerios.length ? ev.ministerios.map((m) => m.nome).join(", ") : "Administração"}
          </p>
        </div>
        {comAcoes && (podeGerirEvento(ev) || (podeGerenciarEventos && ev.status === "pendente")) && (
          <div className="flex shrink-0 items-center gap-1">
            {podeGerenciarEventos && ev.status === "pendente" && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setParaAprovar(ev);
                  }}
                  aria-label="Aprovar evento"
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-success transition-colors hover:bg-success/10"
                >
                  <CheckCircle size={18} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setParaRejeitar(ev);
                  }}
                  aria-label="Recusar evento"
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-danger transition-colors hover:bg-danger/10"
                >
                  <XCircle size={18} />
                </button>
              </>
            )}
            {podeGerirEvento(ev) && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    abrirEdicao(ev);
                  }}
                  aria-label="Editar"
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-info transition-colors hover:bg-info/10"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setParaCancelar(ev);
                  }}
                  aria-label="Cancelar evento"
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-danger transition-colors hover:bg-danger/10"
                >
                  <Ban size={16} />
                </button>
              </>
            )}
          </div>
        )}
      </li>
    );
  }

  return (
    <>
      <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-text">Eventos</h1>
          {podeCriar && <Button onClick={abrirCriacao}>+ Novo evento</Button>}
        </div>
        <p className="mt-1 text-sm text-muted">Programações e atividades da igreja, públicas ou privadas.</p>

        {mensagemSucesso && (
          <p role="status" className="mt-4 rounded-md bg-success/10 px-4 py-3 text-sm text-success">
            {mensagemSucesso}
          </p>
        )}
        {mensagemErro && (
          <p role="alert" className="mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
            {mensagemErro}
          </p>
        )}

        {!!eventos?.length && (
          <div className="mt-4 flex flex-col gap-2 rounded-xl border border-border bg-surface p-3 sm:flex-row sm:flex-wrap sm:items-center">
            <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-muted">
              <Filter size={14} /> Filtrar
            </span>
            <select
              value={filtroMinisterio}
              onChange={(e) => setFiltro("ministerio", e.target.value)}
              className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-text"
            >
              <option value="">Todos os ministérios</option>
              {ministeriosNaLista.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={filtroDia}
              onChange={(e) => setFiltro("dia", e.target.value)}
              aria-label="Filtrar por dia"
              className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-text"
            />
            <select
              value={filtroPeriodo}
              onChange={(e) => setFiltro("periodo", e.target.value)}
              className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-text"
            >
              <option value="">Qualquer horário</option>
              <option value="manha">Manhã</option>
              <option value="tarde">Tarde</option>
              <option value="noite">Noite</option>
            </select>
            {filtrosAtivos && (
              <button
                onClick={limparFiltros}
                className="text-xs font-semibold text-primary hover:underline"
              >
                Limpar filtros
              </button>
            )}
          </div>
        )}

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface">
          {isLoading ? (
            <p className="p-6 text-sm text-muted">Carregando...</p>
          ) : !eventos?.length ? (
            <p className="p-6 text-sm text-muted">Nenhum evento cadastrado.</p>
          ) : !eventosAtivos.length ? (
            <p className="p-6 text-sm text-muted">Nenhum evento ativo no momento.</p>
          ) : (
            <ul>{eventosAtivos.map((ev, idx) => itemEvento(ev, true, idx))}</ul>
          )}
        </div>

        {eventosHistorico.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface">
            <button
              onClick={() => setHistoricoAberto((v) => !v)}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-text"
            >
              <span className="flex items-center gap-2">
                <History size={16} className="text-muted" />
                Histórico — passados, cancelados e recusados ({eventosHistorico.length})
              </span>
              {historicoAberto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {historicoAberto && (
              <>
                <ul className="border-t border-border">
                  {historicoPagina.map((ev, idx) => itemEvento(ev, false, idx))}
                </ul>
                <Paginacao
                  paginaAtual={paginaHistOk}
                  totalPaginas={totalPaginasHist}
                  onMudarPagina={setPaginaHist}
                />
              </>
            )}
          </div>
        )}
      </main>

      <Modal
        titulo={emEdicao ? "Editar evento" : "Novo evento"}
        aberto={modalAberto}
        onClose={() => setModalAberto(false)}
        carregando={criar.isPending || atualizar.isPending}
      >
        <FormularioEvento
          valoresIniciais={emEdicao}
          ambientes={ambientes ?? []}
          ministerios={ministerioOpcoes}
          podeEventoPublico={podeGerenciarEventos}
          carregando={criar.isPending || atualizar.isPending}
          erro={erroForm}
          onSubmit={handleSubmit}
        />
      </Modal>

      <Modal titulo={detalhes?.titulo ?? "Evento"} aberto={!!detalhes} onClose={() => setDetalhes(null)}>
        {detalhes && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                  detalhes.visibilidade === "publico" ? "bg-primary/10 text-primary" : "bg-surface-2 text-muted"
                }`}
              >
                {detalhes.visibilidade === "publico" ? "Público" : "Privado"}
              </span>
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${rotuloStatus(detalhes).cls}`}>
                {rotuloStatus(detalhes).label}
              </span>
              <span className="text-xs text-muted">
                {detalhes.ministerios.length
                  ? detalhes.ministerios.map((m) => m.nome).join(", ")
                  : "Administração"}
              </span>
            </div>

            <div className="flex flex-col gap-1 text-sm text-text">
              <p className="flex items-center gap-1.5">
                <CalendarDays size={14} className="shrink-0 text-muted" />
                {rotuloDiaLongo(detalhes.inicio)}
                {chaveDia(detalhes.fim) !== chaveDia(detalhes.inicio) && ` – ${rotuloDiaLongo(detalhes.fim)}`}
                {!detalhes.dia_inteiro &&
                  (chaveDia(detalhes.fim) === chaveDia(detalhes.inicio)
                    ? ` · ${formatarHora(detalhes.inicio)}–${formatarHora(detalhes.fim)}`
                    : ` · ${formatarHora(detalhes.inicio)} – ${formatarHora(detalhes.fim)}`)}
              </p>
              {detalhes.ambiente_nome && (
                <p className="flex items-center gap-1.5">
                  <MapPin size={14} className="shrink-0 text-muted" />
                  {detalhes.ambiente_nome}
                </p>
              )}
            </div>

            {(detalhes.status === "cancelado" || detalhes.status === "recusado") && detalhes.motivo_exclusao && (
              <p className="rounded-lg bg-surface-2 px-3 py-2 text-xs text-muted">
                <b className="text-text">
                  {detalhes.status === "recusado" ? "Motivo da recusa:" : "Motivo do cancelamento:"}
                </b>{" "}
                {detalhes.motivo_exclusao}
              </p>
            )}

            {detalhes.descricao ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-text">
                {detalhes.descricao}
              </p>
            ) : (
              <p className="text-sm italic text-muted">Sem descrição adicional.</p>
            )}

            <Button type="button" onClick={() => setDetalhes(null)} className="mt-2">
              Fechar
            </Button>
          </div>
        )}
      </Modal>

      <ModalConfirmacao
        aberto={!!paraCancelar}
        titulo="Cancelar evento"
        mensagem={`Cancelar "${paraCancelar?.titulo}"? Ele vai para o histórico (não é apagado) e a sala reservada é liberada.`}
        textoBotaoConfirmar="Cancelar evento"
        variante="perigo"
        carregando={cancelar.isPending}
        erro={erroCancelamento}
        confirmarDesabilitado={!motivoCancelamento.trim()}
        onConfirmar={confirmarCancelamento}
        onCancelar={() => {
          setParaCancelar(null);
          setErroCancelamento(null);
          setMotivoCancelamento("");
        }}
      >
        <label htmlFor="motivo-cancelamento-evento" className="mb-1.5 block text-xs font-semibold text-text">
          Motivo do cancelamento <span className="text-danger">*</span>
        </label>
        <textarea
          id="motivo-cancelamento-evento"
          value={motivoCancelamento}
          onChange={(e) => setMotivoCancelamento(e.target.value)}
          rows={3}
          placeholder="Ex.: evento remarcado para outra data"
          className="w-full resize-none rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
        />
        <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-muted">
          <BellRing size={13} className="mt-px shrink-0 text-primary" />
          {paraCancelar?.ministerios.length
            ? `Os líderes de ${paraCancelar.ministerios.map((m) => m.nome).join(", ")} vão receber este motivo na notificação.`
            : "O motivo fica registrado no cancelamento do evento."}
        </p>
      </ModalConfirmacao>

      <ModalConfirmacao
        aberto={!!paraAprovar}
        titulo="Aprovar evento"
        mensagem={`Aprovar "${paraAprovar?.titulo}"? Ele passa a aparecer normalmente e quem criou é avisado.`}
        textoBotaoConfirmar="Aprovar"
        variante="padrao"
        carregando={aprovar.isPending}
        onConfirmar={confirmarAprovacao}
        onCancelar={() => setParaAprovar(null)}
      />

      <ModalConfirmacao
        aberto={!!paraRejeitar}
        titulo="Recusar evento"
        mensagem={`Recusar a solicitação do evento "${paraRejeitar?.titulo}"? Quem criou será avisado.`}
        textoBotaoConfirmar="Recusar"
        variante="perigo"
        carregando={rejeitar.isPending}
        erro={erroRejeicao}
        confirmarDesabilitado={!motivoRejeicao.trim()}
        onConfirmar={confirmarRejeicao}
        onCancelar={() => {
          setParaRejeitar(null);
          setErroRejeicao(null);
          setMotivoRejeicao("");
        }}
      >
        <label htmlFor="motivo-rejeicao-evento" className="mb-1.5 block text-xs font-semibold text-text">
          Motivo da recusa <span className="text-danger">*</span>
        </label>
        <textarea
          id="motivo-rejeicao-evento"
          value={motivoRejeicao}
          onChange={(e) => setMotivoRejeicao(e.target.value)}
          rows={3}
          placeholder="Ex.: choca com outro evento no mesmo horário"
          className="w-full resize-none rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
        />
        <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-muted">
          <BellRing size={13} className="mt-px shrink-0 text-primary" />
          O motivo vai na notificação para quem criou o evento.
        </p>
      </ModalConfirmacao>
    </>
  );
}
